import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

const TermsOfService = () => {
  const brand = useBrand();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to {brand.name}</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 sm:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 mb-8">Last updated: January 2026</p>

          <div className="prose prose-slate max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <p className="text-slate-600 leading-relaxed">
                Welcome to {brand.legal.companyName} ("Platform," "we," "us," or "our"). By accessing or using our services,
                you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
              </p>
            </section>

            {/* 1. Acceptance of Terms */}
            <section className="mb-8" id="acceptance">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                By creating an account, uploading content, or using any part of our Platform, you acknowledge that
                you have read, understood, and agree to be bound by these Terms and our Privacy Policy.
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You must be at least 18 years old or have parental/guardian consent to use this Platform.</li>
                <li>We reserve the right to modify these Terms at any time. Continued use after changes constitutes acceptance.</li>
                <li>If you do not agree to these Terms, do not use the Platform.</li>
              </ul>
            </section>

            {/* 2. Service Description */}
            <section className="mb-8" id="service">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">2. Service Description</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                {brand.legal.companyName} is an AI-powered merchandise creation platform that enables users to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Upload images or generate designs using artificial intelligence</li>
                <li>Create custom merchandise products</li>
                <li>List products on our marketplace, which connects to third-party print-on-demand providers</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                Products are manufactured and fulfilled by third-party print providers.
                We do not guarantee availability of specific products, colors, or sizes.
              </p>
            </section>

            {/* 3. User Accounts */}
            <section className="mb-8" id="accounts">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">3. User Accounts</h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You must provide accurate and complete information when creating an account.</li>
                <li>You are responsible for maintaining the security of your account credentials.</li>
                <li>You are responsible for all activities that occur under your account.</li>
                <li>We may suspend or terminate accounts that violate these Terms or for any reason at our discretion.</li>
                <li>You may delete your account at any time by contacting {brand.legal.supportEmail}.</li>
              </ul>
            </section>

            {/* 4. User-Generated Content */}
            <section className="mb-8" id="content">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">4. User-Generated Content</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>Ownership:</strong> You retain all ownership rights to the original content you upload to the Platform.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>License Grant:</strong> By uploading content, you grant us a worldwide, non-exclusive, royalty-free
                license to use, reproduce, modify, and display your content solely for the purpose of providing our services
                (e.g., creating product mockups, fulfilling orders).
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>Your Responsibilities:</strong> You represent and warrant that:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>You own or have the necessary rights to use and authorize use of all content you upload.</li>
                <li>Your content does not infringe any third-party intellectual property rights (copyrights, trademarks, etc.).</li>
                <li>Your content does not violate any laws or regulations.</li>
                <li>Your content is not defamatory, obscene, threatening, or otherwise objectionable.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>Content Removal:</strong> We reserve the right to remove any content at our sole discretion,
                without notice, for any reason including suspected infringement or Terms violations.
              </p>
            </section>

            {/* 5. AI-Generated Content */}
            <section className="mb-8" id="ai-content">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">5. AI-Generated Content</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                Our Platform uses artificial intelligence (including third-party AI services) to generate images and designs.
                By using AI features, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>AI-generated images may not be perfect, accurate, or suitable for your intended purpose.</li>
                <li>We make no guarantees regarding the quality, accuracy, or originality of AI-generated content.</li>
                <li>AI-generated content may inadvertently resemble existing works; you are responsible for reviewing outputs before use.</li>
                <li>Once you accept and use an AI-generated design, you assume full responsibility for that content.</li>
                <li>AI services are subject to usage limits and may be modified or discontinued at any time.</li>
              </ul>
            </section>

            {/* 6. Prohibited Uses */}
            <section className="mb-8" id="prohibited">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">6. Prohibited Uses</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                You agree NOT to use the Platform to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Upload content that infringes copyrights, trademarks, or other intellectual property rights.</li>
                <li>Create products featuring copyrighted characters, logos, or designs you don't have rights to use.</li>
                <li>Upload or generate hateful, discriminatory, violent, or illegal content.</li>
                <li>Create misleading, fraudulent, or deceptive products.</li>
                <li>Use automated tools, bots, or scripts to abuse our services.</li>
                <li>Circumvent rate limits, security measures, or access restrictions.</li>
                <li>Resell or redistribute our services without authorization.</li>
                <li>Interfere with the Platform's operation or other users' access.</li>
              </ul>
            </section>

            {/* 7. Payment & Pricing */}
            <section className="mb-8" id="payment">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">7. Payment & Pricing</h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>All prices are displayed in USD and are subject to change without notice.</li>
                <li>Payment processing is handled securely by Stripe. We do not store your payment card information.</li>
                <li>Prices include product costs and Platform fees. Shipping costs are calculated at checkout.</li>
                <li>All sales are final once an order is submitted for production.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>Refunds:</strong> Due to the custom, made-to-order nature of products, refunds are generally
                not available. Any refund, if granted at our sole discretion, shall be limited to the product purchase
                price less any applicable fees and costs. Refunds are not guaranteed and are subject to verification
                and approval. {brand.legal.companyName} reserves the right to deny any refund request. Quality or fulfillment issues
                must be claimed through the third-party print provider's policies.
              </p>
              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>Creator Commissions:</strong> If you participate in our affiliate or creator program, commission
                payouts are subject to minimum thresholds, verification, and our commission policies. We reserve the right
                to withhold, reverse, or reclaim commissions in cases of fraud, Terms violations, order cancellations,
                refunds, chargebacks, disputed transactions, or at our sole discretion for any reason we deem appropriate.
              </p>
            </section>

            {/* 8. Fulfillment Disclaimer */}
            <section className="mb-8" id="fulfillment">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">8. Fulfillment & Shipping</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                All products are manufactured and shipped by third-party print providers.
                {brand.legal.companyName} acts as a facilitator and does not manufacture products directly.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3 font-medium bg-amber-50 border border-amber-200 rounded-lg p-3">
                <strong>IMPORTANT:</strong> {brand.legal.companyName} is a technology platform that connects users with third-party
                print providers. We do not manufacture, handle, inspect, or ship any physical products. All products
                are created, quality-controlled, and shipped entirely by independent print providers. {brand.legal.companyName} has
                no control over and assumes no responsibility for product quality, accuracy, safety, or fitness for
                any purpose.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>We are NOT responsible for:</strong>
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Shipping delays caused by carriers, customs, or circumstances beyond our control.</li>
                <li>Print quality variations that fall within industry standards.</li>
                <li>Product defects (contact the print provider directly to file a claim).</li>
                <li>Lost or stolen packages after delivery confirmation.</li>
                <li>Incorrect shipping addresses provided by customers.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                For fulfillment issues, contact the print provider directly or email
                {brand.legal.supportEmail} for guidance on filing a claim. {brand.legal.companyName} acts solely as an intermediary
                and cannot guarantee any resolution, replacement, or refund. Any remedy is at the sole discretion
                of the third-party print provider.
              </p>
            </section>

            {/* 9. Limitation of Liability */}
            <section className="mb-8" id="liability">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">9. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed mb-3 uppercase font-medium">
                THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
                EITHER EXPRESS OR IMPLIED.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                To the maximum extent permitted by law, {brand.legal.companyName} and its owners, operators, affiliates, and partners
                shall NOT be liable for:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Any indirect, incidental, special, consequential, or punitive damages.</li>
                <li>Loss of profits, revenue, data, or business opportunities.</li>
                <li>Service interruptions, errors, or security breaches.</li>
                <li>Actions or inactions of third-party service providers (print providers, payment processors, etc.).</li>
                <li>User-generated or AI-generated content and its consequences.</li>
                <li>Any claims arising from your use of the Platform.</li>
                <li>Any claims related to product quality, safety, materials, or manufacturing defects.</li>
                <li>Any personal injury, property damage, or other harm arising from use of products.</li>
                <li>Any allergic reactions, skin irritation, or health issues from product materials.</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-3">
                <strong>Maximum Liability:</strong> In no event shall our total liability exceed the amount actually
                paid by you for the specific product or service giving rise to the claim, less any applicable fees
                and costs. This limitation applies to all claims regardless of legal theory.
              </p>
            </section>

            {/* 10. Indemnification */}
            <section className="mb-8" id="indemnification">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">10. Indemnification</h2>
              <p className="text-slate-600 leading-relaxed">
                You agree to indemnify, defend, and hold harmless {brand.legal.companyName}, its owners, operators, affiliates,
                and partners from and against any claims, damages, losses, liabilities, costs, and expenses
                (including reasonable attorneys' fees) arising from or related to:
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-3">
                <li>Your use of the Platform.</li>
                <li>Content you upload or create.</li>
                <li>Your violation of these Terms.</li>
                <li>Your violation of any third-party rights, including intellectual property rights.</li>
                <li>Any claims that your content caused damage to a third party.</li>
                <li>Any product liability claims arising from products you designed or sold.</li>
                <li>Any claims that products you created are unsafe, defective, or harmful.</li>
              </ul>
            </section>

            {/* 11. Dispute Resolution */}
            <section className="mb-8" id="disputes">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">11. Dispute Resolution</h2>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>Governing Law:</strong> These Terms shall be governed by and construed in accordance with
                the laws of the State of California, United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>Binding Arbitration:</strong> Any dispute, claim, or controversy arising out of or relating to
                these Terms or your use of the Platform shall be resolved through binding arbitration administered by
                JAMS or the American Arbitration Association (AAA) under their applicable rules. The arbitration shall
                take place in California. The arbitrator's decision shall be final and binding.
              </p>
              <p className="text-slate-600 leading-relaxed mb-3">
                <strong>Class Action Waiver:</strong> YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE LIMITED TO
                THE DISPUTE BETWEEN US AND YOU INDIVIDUALLY. YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT
                OR CLASS-WIDE ARBITRATION.
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Small Claims Exception:</strong> Notwithstanding the above, either party may bring an individual
                action in small claims court if the claim qualifies.
              </p>
            </section>

            {/* 12. Termination */}
            <section className="mb-8" id="termination">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">12. Termination</h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>We may suspend or terminate your account at any time, with or without cause, with or without notice.</li>
                <li>Upon termination, your right to use the Platform ceases immediately.</li>
                <li>You may terminate your account by contacting {brand.legal.supportEmail}.</li>
                <li>Provisions that by their nature should survive termination shall survive (including indemnification,
                    limitation of liability, and dispute resolution).</li>
              </ul>
            </section>

            {/* 13. Changes to Terms */}
            <section className="mb-8" id="changes">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">13. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of material changes by
                updating the "Last updated" date at the top of this page. Your continued use of the Platform after
                changes are posted constitutes acceptance of the modified Terms.
              </p>
            </section>

            {/* 14. Contact */}
            <section className="mb-8" id="contact">
              <h2 className="text-xl font-semibold text-slate-900 mb-3">14. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed">
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-slate-600 mt-3">
                <strong>Email:</strong>{' '}
                <a href={`mailto:${brand.legal.supportEmail}`} className="text-blue-600 hover:text-blue-700">
                  {brand.legal.supportEmail}
                </a>
              </p>
            </section>

            {/* Acknowledgment */}
            <section className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-slate-500 text-sm italic">
                By using {brand.legal.companyName}, you acknowledge that you have read, understood, and agree to be bound by
                these Terms of Service and our{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-700">Privacy Policy</Link>.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-slate-500 text-sm">
            &copy; {brand.legal.copyrightYear} {brand.legal.companyName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
